import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import { ProjectCard } from '../components/ProjectCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero slideshow images with animation variants
const HERO_SLIDES = [
  {
    url: "https://customer-assets.emergentagent.com/job_77c7e095-6aee-4db7-89db-d1905485427a/artifacts/pkxn58eu_First%20%282%29.jpeg",
    animation: "zoomIn",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_77c7e095-6aee-4db7-89db-d1905485427a/artifacts/nnn1o1ss_Second.jpeg",
    animation: "panRight",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_77c7e095-6aee-4db7-89db-d1905485427a/artifacts/5bnz2r17_Third.png",
    animation: "zoomOut",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_77c7e095-6aee-4db7-89db-d1905485427a/artifacts/tohzex3z_Fourth.jpeg",
    animation: "panLeft",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_77c7e095-6aee-4db7-89db-d1905485427a/artifacts/s8cl263g_Fifth.png",
    animation: "zoomIn",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_77c7e095-6aee-4db7-89db-d1905485427a/artifacts/419sdhmg_sixth.jpeg",
    animation: "panRight",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_77c7e095-6aee-4db7-89db-d1905485427a/artifacts/fcdrxe3j_Seventh.jpeg",
    animation: "zoomOut",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_77c7e095-6aee-4db7-89db-d1905485427a/artifacts/qhstg3se_eighth.jpeg",
    animation: "panLeft",
  },
];

// Animation variants for Ken Burns effect
const slideAnimations = {
  zoomIn: {
    initial: { scale: 1.15, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.95, opacity: 0 },
  },
  zoomOut: {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1.1, opacity: 1 },
    exit: { scale: 1.15, opacity: 0 },
  },
  panRight: {
    initial: { x: '-5%', scale: 1.1, opacity: 0 },
    animate: { x: '5%', scale: 1.1, opacity: 1 },
    exit: { x: '10%', scale: 1.05, opacity: 0 },
  },
  panLeft: {
    initial: { x: '5%', scale: 1.1, opacity: 0 },
    animate: { x: '-5%', scale: 1.1, opacity: 1 },
    exit: { x: '-10%', scale: 1.05, opacity: 0 },
  },
};

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects?featured=true`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Navigate to next slide
  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
  }, []);

  // Navigate to specific slide
  const goToSlide = useCallback((index) => {
    setDirection(index > currentSlide ? 1 : -1);
    setCurrentSlide(index);
  }, [currentSlide]);

  // Auto-advance slideshow
  useEffect(() => {
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const currentAnimation = slideAnimations[HERO_SLIDES[currentSlide].animation];

  return (
    <div data-testid="home-page">
      {/* Hero Slideshow Section */}
      <section className="relative h-screen overflow-hidden" data-testid="hero-section">
        {/* Background Slideshow with Dynamic Ken Burns Effect */}
        <div className="absolute inset-0">
          <AnimatePresence mode="sync">
            <motion.div
              key={currentSlide}
              className="absolute inset-0"
              initial={currentAnimation.initial}
              animate={currentAnimation.animate}
              exit={currentAnimation.exit}
              transition={{ 
                duration: 6,
                ease: [0.25, 0.1, 0.25, 1],
                opacity: { duration: 1.2 }
              }}
            >
              <img
                src={HERO_SLIDES[currentSlide].url}
                alt={`Architecture showcase ${currentSlide + 1}`}
                className="w-full h-full object-cover"
              />
            </motion.div>
          </AnimatePresence>
          
          {/* Layered gradient overlay for depth */}
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/30 via-transparent to-stone-900/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/20 via-transparent to-stone-900/20" />
        </div>

        {/* Slide Indicators with progress animation */}
        <div className="absolute bottom-32 left-1/2 -translate-x-1/2 flex gap-3 z-10">
          {HERO_SLIDES.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              data-testid={`slide-indicator-${index}`}
              className="relative h-1 rounded-full overflow-hidden transition-all duration-300"
              style={{ width: index === currentSlide ? '48px' : '16px' }}
              aria-label={`Go to slide ${index + 1}`}
            >
              <span className="absolute inset-0 bg-white/30" />
              {index === currentSlide && (
                <motion.span
                  className="absolute inset-0 bg-white origin-left"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 6, ease: 'linear' }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-24 md:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <h1 className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-light text-white tracking-tight leading-tight mb-8">
              Beyond the blueprint
              <br />
              <span className="italic">into the being</span>
            </h1>
            <Link
              to="/projects"
              data-testid="hero-cta"
              className="inline-flex items-center gap-3 rounded-full px-8 py-4 text-xs uppercase tracking-widest font-manrope font-medium bg-transparent border border-white text-white hover:bg-white hover:text-stone-900 transition-colors duration-300"
            >
              View Projects
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-[1px] h-12 bg-white/50"
          />
        </motion.div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24" data-testid="featured-projects">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-16"
        >
          <div>
            <h2 className="font-manrope text-3xl md:text-4xl font-light tracking-tight text-stone-900">
              Portfolio
            </h2>
          </div>
          <Link
            to="/projects"
            data-testid="view-all-projects"
            className="mt-6 md:mt-0 inline-flex items-center gap-2 font-manrope text-xs uppercase tracking-[0.2em] font-medium text-stone-900 hover:text-stone-500 transition-colors duration-300"
          >
            View All
            <ArrowRight size={14} />
          </Link>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="projects-loading">
            {[...Array(4)].map((_, i) => (
              <div key={i}>
                <div className="bg-stone-100 animate-pulse aspect-[16/10]" />
                <div className="mt-6 space-y-2">
                  <div className="h-6 bg-stone-100 animate-pulse w-3/4" />
                  <div className="h-4 bg-stone-100 animate-pulse w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
            {projects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={index}
              />
            ))}
          </div>
        )}
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32 bg-stone-100" data-testid="philosophy-section">
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <span className="font-dm-sans text-xs text-stone-400 tracking-wide uppercase mb-4 block">
                Our Philosophy
              </span>
              <h2 className="font-manrope text-3xl md:text-4xl font-light tracking-tight text-stone-900 mb-8">
                Design with
                <br />
                <span className="italic">intention</span>
              </h2>
              <p className="font-dm-sans text-stone-600 leading-relaxed mb-6">
                At BIOM, we believe architecture is more than buildings—it's the art of creating environments that enhance human experience. Every project begins with intent: listening closely, understanding context and uncovering the unique narrative each space holds.
              </p>
              <p className="font-dm-sans text-stone-600 leading-relaxed mb-8">
                We don't just design structures; we create environments that enhance how people live, work, and connect. Through a blend of classic proportions and modern ingenuity, we deliver spaces that feel both intentional and transcendent.
              </p>
              <Link
                to="/about"
                data-testid="learn-more-link"
                className="inline-flex items-center gap-2 rounded-full px-8 py-3 text-xs uppercase tracking-widest font-manrope font-medium border border-stone-300 text-stone-900 hover:bg-stone-900 hover:text-white hover:border-stone-900 transition-colors duration-300"
              >
                Learn More
                <ArrowRight size={14} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden bg-stone-200">
                <img
                  src="https://customer-assets.emergentagent.com/job_minimal-arch-5/artifacts/rcbshl01_10.jpg.jpeg"
                  alt="Architecture detail"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-stone-300" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 lg:py-40 px-6 md:px-12 lg:px-24" data-testid="cta-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center max-w-2xl mx-auto"
        >
          <h2 className="font-manrope text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-stone-900 mb-6">
            Let's create together
          </h2>
          <p className="font-dm-sans text-stone-500 leading-relaxed mb-10">
            Have a project in mind? We'd love to hear about your vision and explore how we can bring it to life.
          </p>
          <Link
            to="/contact"
            data-testid="contact-cta"
            className="inline-flex items-center gap-3 rounded-full px-10 py-4 text-xs uppercase tracking-widest font-manrope font-medium bg-stone-900 text-white hover:bg-stone-800 transition-colors duration-300"
          >
            Start a Conversation
            <ArrowRight size={16} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
